import * as dmUtils from '@wix/document-manager-utils'
import {saveErrorCodes, searchRequestId, getXSeenBy, ReportableError} from '@wix/document-manager-utils'
import _ from 'lodash'
import errorConstants from '../errors/errors'
import {staticCodeMapping} from './saveTasks/saveDocumentBase'
import type {CSaveApi} from '@wix/document-manager-extensions'
import type {PS} from '@wix/document-services-types'

const presaveOperationsRejectedMsg = 'DocumentServices presave operations were rejected by server.'
const pendingCsaveRejectedMsg = 'A pending cSave was rejected by server.'
const saveDisabledMsg = 'DocumentServices was created with parameter disableSave=true, so save is disabled.'
const saveInProgressMsg = 'Save is already in progress. The last save request is being ignored.'

/**
 * information about the failure of a specific service within the process
 */
export interface ErrorInfo {
    errorInfo?: any
    errorCode: number | string
    errorType: string
    errorDescription: string
    requestId?: string
    xSeenBy?: string
}

/**
 * A map of 'serviceName: errorInfo'
 */
// export type FailInfo = Record<string, ErrorInfo>
export interface DSError {
    documentServices?: ErrorInfo
    document?: ErrorInfo
    preSaveAsTemplateOperation?: Error
    preSaveOperation?: Error | ReportableError
    isPresaveOperationsRejected?: boolean
    isWaitForPendingCsaveRejected?: boolean
}

export interface DSErrorWithReportableErrorDetails {
    dsError: DSError
    reportableErrorDetails?: {
        tags?: Record<string, any>
    }
}

const error = (
    errorType: string,
    errorDescription: string,
    errorCode: number | string,
    isPresaveOperationsRejected?: boolean
): DSError => ({
    documentServices: {
        errorCode,
        errorType,
        errorDescription
    },
    ...(isPresaveOperationsRejected ? {isPresaveOperationsRejected} : {})
})

const USER_NOT_AUTHORIZED_FOR_SITE = error(
    errorConstants.save.USER_NOT_AUTHORIZED_FOR_SITE,
    saveDisabledMsg,
    'USER_NOT_AUTHORIZED_FOR_SITE'
)
const SAVE_DISABLED_IN_DOCUMENT_SERVICES = error(
    errorConstants.save.SAVE_DISABLED_IN_DOCUMENT_SERVICES,
    saveDisabledMsg,
    dmUtils.clientSaveErrors.SAVE_DISABLED_IN_DOCUMENT_SERVICES
)
const SAVE_IN_PROGRESS = error(
    errorConstants.save.SAVE_IN_PROGRESS,
    saveInProgressMsg,
    dmUtils.clientSaveErrors.SAVE_IN_PROGRESS
)

const createDSErrorFromCsaveError = (ps: PS, e: any, errorMessage: string) => {
    const extensionAPI = ps.extensionAPI as CSaveApi
    const userInfo = ps.dal.get(ps.pointers.documentServicesModel.getUserInfo())
    const dsError = extensionAPI.continuousSave.cSaveErrors.convertToDSError(e, userInfo) as DSError
    const requestId = e.extras?.requestId || e.reason?.extras?.requestId
    if (requestId) {
        dsError.document.requestId = requestId
    }
    if (!dsError.document?.errorDescription) {
        const msg = e.message || e.details?.applicationError?.code || errorMessage
        dsError.document.errorDescription = msg
    }
    return dsError
}

const createPresaveError = (ps: PS, e: any) => {
    const dsError = createDSErrorFromCsaveError(ps, e, presaveOperationsRejectedMsg)
    dsError.isPresaveOperationsRejected = true
    return dsError
}

const createWaitForPendingCsavesError = (ps: PS, e: any) => {
    const dsError = createDSErrorFromCsaveError(ps, e, pendingCsaveRejectedMsg)
    dsError.isWaitForPendingCsaveRejected = true
    return dsError
}

function isSaveValidationError(errorDescription: DSError) {
    return errorDescription?.document?.errorCode === -10104
}

const getErrorDetails = (e: DSError) => {
    return e.document ?? e.documentServices
}

function normalizeError(e: DSError) {
    const shouldRestore = (e.preSaveOperation as ReportableError)?.tags?.shouldRestoreRevision
    const details = getErrorDetails(e)
    if (!details) {
        e.documentServices = {
            errorCode: shouldRestore
                ? dmUtils.saveErrorCodes.CORRUPTED_TRANSACTIONS_ERROR
                : dmUtils.clientSaveErrors.UNKNOWN_SERVER_ERROR,
            errorDescription: 'unknown save error',
            errorType: shouldRestore ? dmUtils.saveErrors.CORRUPTED_TRANSACTIONS_ERROR : 'unknownSaveError'
        }
        return e
    }
    details.errorCode = shouldRestore
        ? dmUtils.saveErrorCodes.CORRUPTED_TRANSACTIONS_ERROR
        : details.errorCode ?? dmUtils.clientSaveErrors.UNKNOWN_SERVER_ERROR
    details.errorDescription = details.errorDescription ?? 'unknown save error'
    details.errorType = shouldRestore
        ? dmUtils.saveErrors.CORRUPTED_TRANSACTIONS_ERROR
        : details.errorType ?? 'unknownSaveError'
    return e
}

export const getErrorTypeAndMessage = (e: DSError): {errorType: string; message: string} => {
    return {
        errorType: getErrorDetails(e)?.errorType ?? 'unknownSaveError',
        message: getErrorDetails(e)?.errorDescription ?? 'unknown save error'
    }
}

export function convertToDSError(e: string | DSError): DSError {
    if (_.isString(e)) {
        return {
            documentServices: {
                errorCode: '',
                errorType: e,
                errorDescription: e
            }
        }
    }
    return e
}

const getSiteUnavailable = (errorDescription: string, requestId: string): ErrorInfo => ({
    errorCode: saveErrorCodes.SITE_UNAVAILABLE,
    errorType: errorConstants.save.SITE_UNAVAILABLE,
    errorDescription,
    requestId
})

const codeToType = errorCode => (errorCode in staticCodeMapping ? staticCodeMapping[errorCode] : errorCode)

const createHttpError = (errorInfo: ErrorInfo, e: any): ErrorInfo =>
    _.defaults(errorInfo, {
        requestId: searchRequestId(e),
        xSeenBy: getXSeenBy(e)
    })

export const convertHttpError = async (e: any /*Error | Response | ServerErrorData*/): Promise<ErrorInfo> => {
    const details = e.details || e.result?.details
    if (details?.applicationError?.code /* || e instanceof ServerError*/) {
        const errorCode = details?.applicationError?.code
        const errorDescription = details?.applicationError?.description
        return createHttpError(
            {
                errorCode,
                errorType: codeToType(errorCode),
                errorDescription,
                requestId: e.extras.requestId,
                xSeenBy: e.extras.xSeenBy
            },
            e
        )
    }
    if (e.status === 404) {
        return getSiteUnavailable(e.statusText || e.message || 'Site unavailable', searchRequestId(e))
    }
    if (e.headers?.get('Content-Type') === 'application/json') {
        try {
            const json = await e.json()
            const {errorCode, errorDescription} =
                'details' in json
                    ? {
                          errorCode: json.details?.applicationError?.code,
                          errorDescription: json.details?.applicationError?.description
                      }
                    : {errorCode: json.errorCode, errorDescription: json.errorDescription}

            return createHttpError(
                {
                    errorCode,
                    errorType: codeToType(errorCode),
                    errorDescription
                },
                e
            )
        } catch (e1) {
            // ignore
        }
    }

    return createHttpError(
        {
            errorCode: e.status,
            errorType: errorConstants.save.HTTP_REQUEST_ERROR,
            errorDescription: e.statusText
        },
        e
    )
}

export default {
    USER_NOT_AUTHORIZED_FOR_SITE,
    SAVE_DISABLED_IN_DOCUMENT_SERVICES,
    SAVE_IN_PROGRESS,
    createPresaveError,
    isSaveValidationError,
    normalizeError,
    createWaitForPendingCsavesError
}
